/**
 * Source Expansion - Handles expansion of Chariot group sources
 *
 * Expands the special "chariot" source value into specific source values
 * by filtering out integration sources from the group.
 *
 * @module queryBuilderCore/utils/sourceExpansion
 */

import type { DropdownOption } from '@/types/dropdown.types';

type GroupOption = DropdownOption & {
  isGroup?: boolean;
  items?: DropdownOption[];
};

/**
 * Expands "chariot" source value into specific Chariot source values,
 * excluding integration sources
 *
 * @param values - Source value(s) to expand
 * @param entityType - Type of entity (Asset or Vulnerability)
 * @param assetFilterOptions - Available asset filter options
 * @param vulnFilterOptions - Available vulnerability filter options
 * @param integrationValues - List of integration source values to exclude
 * @returns Expanded source values
 */
export function expandChariotGroupInSourceValues(
  values: string | string[],
  entityType: string,
  assetFilterOptions?: Record<string, unknown>,
  vulnFilterOptions?: Record<string, unknown>,
  integrationValues?: string[]
): string | string[] {
  const valueArray = Array.isArray(values) ? values : [values];

  let sourceFilters: GroupOption[] = [];

  if (entityType === 'Asset' && assetFilterOptions?.source) {
    sourceFilters = assetFilterOptions.source as GroupOption[];
  } else if (
    entityType === 'Vulnerability' &&
    vulnFilterOptions?.sourceFilters
  ) {
    sourceFilters = vulnFilterOptions.sourceFilters as GroupOption[];
  }

  if (sourceFilters.length === 0) {
    return values;
  }

  const chariotGroup = sourceFilters.find(
    (option: GroupOption) =>
      option.isGroup === true && option.label === 'Chariot'
  );

  let expandedValues: string[] = [];

  if (
    valueArray.includes('chariot') &&
    chariotGroup?.items &&
    chariotGroup.items.length > 0
  ) {
    const otherSelectedValues = valueArray.filter(v => v !== 'chariot');

    const integrationValuesArray = integrationValues || [
      'amazon',
      'azure',
      'gcp',
      'tenablevm',
      'wiz',
      'github',
      'qualys',
      'microsoft-defender',
      'crowdstrike',
    ];
    const chariotValues = chariotGroup.items
      .filter((item: DropdownOption) => {
        const value = item.value?.toString().toLowerCase() || '';
        return !integrationValuesArray.includes(value);
      })
      .map((item: DropdownOption) => item.value?.toString() || '');

    expandedValues = [...otherSelectedValues, ...chariotValues];
  } else {
    expandedValues = valueArray;
  }

  if (valueArray.includes('chariot') && chariotGroup?.items) {
    return expandedValues;
  }

  return Array.isArray(values) ? expandedValues : expandedValues[0] || '';
}
